/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  getSingletonInsightDvMarkerSeries,
  getDataItemValue
} from '../../helper/standardChart';
import { handleAnimation } from '../helper';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 y 轴指标起始数据值 */
  yStart: string;
  /** 要标记的 y 轴指标终止数据值 */
  yEnd: string;
  /** 要标记的 y 轴指标起始文本描述 */
  yStartSummary: string;
  /** 要标记的 y 轴指标终止文本描述 */
  yEndSummary: string;
  /** 文本描述 */
  summary: string;
  /** 文本摘要 @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker/#seriesdvitemstyle */
  textSummary?: unknown;
  /** 边界线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  line?: unknown;
  /** 边界线 @see https://echarts.apache.org/zh/option.html#series-bar.markArea.data */
  area?: unknown;
  /** 出现动画时长，时长为 0 时关闭动画 */
  animationDuration?: number;
};

const type = 'Interval';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        series.markArea = series.markArea || {};
        series.markArea.data = (series.markArea.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
    }
  },
  /** @see https://datav.iwencai.com/example.html#/static-page?id=252 */
  render: {
    onOptionUpdate(context, option) {
      const insight = context.insightOption;
      const { animationDuration = 0 } = insight;

      function render(data: InsightData) {
        const {
          x,
          y,
          yStart,
          yEnd,
          yStartSummary,
          yEndSummary,
          summary,
          textSummary: textSummaryOpt = {},
          line: lineOpt = {}
        } = data;

        const startDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          yValue: yStart
        });
        if (!startDataItemMetaInfo) {
          return;
        }
        const endDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          yValue: yEnd
        });
        if (!endDataItemMetaInfo) {
          return;
        }
        const { xAxisIndex, yAxisIndex, seriesIndex } = startDataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];

        const startLine = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: yStartSummary,
          dvLabelText: yStartSummary,
          yAxis: yStart,
          __insightFlag: INSIGHT_FLAG
        };

        const endLine = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: yEndSummary,
          dvLabelText: yEndSummary,
          yAxis: yEnd,
          __insightFlag: INSIGHT_FLAG
        };

        series.markLine.data.push(startLine, endLine);

        const markerSeries = getSingletonInsightDvMarkerSeries(option, { xAxisIndex, yAxisIndex });
        const xAxis = context.util.data.toArray(option.xAxis)[xAxisIndex]!;
        /**
         * 文本描述标签的定位：
         * x：最后一个数据点
         * y：Interval的较小点
         * TODO:暂未考虑yStart和yEnd为非数值类型的场景
         */
        const endPointSummary = Math.min(Number(yStart), Number(yEnd));

        const pointSummary = {
          value: [getDataItemValue(xAxis.data[xAxis.data.length - 1]), endPointSummary],
          itemStyle: context.util.data.deepMerge(
            {
              textStyle: {
                style: {
                  text: summary
                }
              }
            },
            textSummaryOpt
          )
        };
        markerSeries.data.push(pointSummary as unknown as number);
        handleAnimation(series.markLine, animationDuration, markerSeries);
      }

      function renderArea(data: InsightData) {
        const { x, y, yStart, yEnd, area: areaOpt = {} } = data;

        const startDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          yValue: yStart
        });
        if (!startDataItemMetaInfo) {
          return;
        }
        const endDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          yValue: yEnd
        });
        if (
          !endDataItemMetaInfo ||
          startDataItemMetaInfo.seriesIndex !== endDataItemMetaInfo.seriesIndex
        ) {
          return;
        }
        const { seriesIndex } = startDataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];

        series.markArea = series.markArea || {};
        series.markArea.data = series.markArea.data || [];

        const area = [
          {
            ...(areaOpt as any),
            yAxis: yStart
          },
          {
            ...(areaOpt as any),
            yAxis: yEnd
          }
        ];
        area.__insightFlag = INSIGHT_FLAG;

        series.markArea.data.push(area);
      }

      insight.data.forEach(item => {
        render(item);
        renderArea(item);
      });
    }
  }
});
